import { useEffect, useMemo, useState } from "react";
import { ApiError, type Invite, type PublicUser, type Uuid } from "@shared/api";
import { fromPublic, Tile } from "@entities/user";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { inviteLink } from "@shared/lib/jump";
import { IconChevronDown, IconVoiceChannel } from "@shared/ui/icons";
import { ownLink, sendLink } from "../lib/link";

type Sent = "busy" | "sent" | "failed";

/**
 * «Пригласить друзей на сервер» — как в Discord: друзья с кнопкой
 * «Пригласить», внизу ссылка с «Копировать».
 *
 * Ссылка одна на всё окно. Своя живая (без предела, не на исходе) берётся
 * готовой, иначе заводится новая на неделю: каждое открытие окна не должно
 * оставлять в списке приглашений ещё одну строку.
 *
 * Для голосового канала — как в Discord: «Участники окажутся в 🔊канал», те,
 * кто уже в звонке, не предлагаются, а остальные делятся на «Участники
 * сервера» и «Пригласить на сервер». Звать в голос можно и своих по серверу:
 * ссылка ведёт в канал.
 */
export function InviteDialog({
  serverId,
  serverName,
  meId,
  channel,
  friends,
  members,
  voice = false,
  inCall,
  voiceOf,
  onClose,
}: {
  serverId: Uuid;
  serverName: string;
  meId: Uuid;
  /** Куда ведёт ссылка — открытый канал этого сервера, как в Discord. */
  channel: { id: Uuid; name: string } | null;
  /** Кого предлагать: друзья, а для голосового — ещё и те, с кем есть переписка. */
  friends: PublicUser[];
  /** Кто уже на сервере: на сервер им приглашение ни к чему. */
  members: Set<Uuid>;
  /** Канал со звонком: приглашают в голос, а не на сервер. */
  voice?: boolean;
  /** Кто уже в этом звонке — их не предлагаем. */
  inCall?: Set<Uuid>;
  /** В каком голосовом канале человек сейчас — подпись под именем, как в Discord. */
  voiceOf?: (id: Uuid) => string | null;
  onClose: () => void;
}) {
  const [link, setLink] = useState<Invite | null>(null);
  const code = link?.code ?? null;
  const [problem, setProblem] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sent, setSent] = useState<Record<Uuid, Sent>>({});
  const [copied, setCopied] = useState(false);
  /** Свёрнутые разделы окна голосового канала. */
  const [shut, setShut] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;

    ownLink(serverId, meId, channel?.id ?? null)
      .then((invite) => {
        if (alive) setLink(invite);
      })
      .catch((error: unknown) => {
        if (!alive) return;
        setProblem(
          error instanceof ApiError && error.status === 403
            ? "На этом сервере у вас нет права приглашать"
            : "Ссылка не создалась",
        );
      });

    return () => {
      alive = false;
    };
  }, [serverId, meId, channel?.id]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return friends
      .filter((friend) => friend.id !== meId)
      .filter((friend) => (voice ? !inCall?.has(friend.id) : !members.has(friend.id)))
      .filter(
        (friend) =>
          !needle ||
          (friend.display_name ?? "").toLowerCase().includes(needle) ||
          friend.handle.toLowerCase().includes(needle),
      );
  }, [friends, members, meId, voice, inCall, query]);

  /** Ссылка уходит другу в личку, окно остаётся открытым — как в Discord. */
  async function invite(friend: PublicUser) {
    if (!code) return;
    setSent((previous) => ({ ...previous, [friend.id]: "busy" }));
    try {
      await sendLink(friend.id, code);
      setSent((previous) => ({ ...previous, [friend.id]: "sent" }));
    } catch {
      setSent((previous) => ({ ...previous, [friend.id]: "failed" }));
    }
  }

  function copy() {
    if (!code) return;
    void navigator.clipboard
      ?.writeText(inviteLink(code))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }

  function row(friend: PublicUser) {
    const state = sent[friend.id];
    const where = voiceOf?.(friend.id) ?? null;
    return (
      <div key={friend.id} className="invite-friend">
        <Tile person={fromPublic(friend)} size={32} />
        <div className="grow invite-friend-name">
          <div className="ellipsis">{friend.display_name ?? friend.username}</div>
          {where ? (
            <div className="tiny ellipsis invite-friend-voice">
              <IconVoiceChannel size={12} />
              {where}
            </div>
          ) : (
            <div className="tiny muted ellipsis">{friend.handle}</div>
          )}
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={!code || state === "busy" || state === "sent"}
          onClick={() => void invite(friend)}
        >
          {state === "sent" ? "Отправлено" : state === "failed" ? "Ещё раз" : "Пригласить"}
        </Button>
      </div>
    );
  }

  /** Раздел окна голосового канала — заголовок сворачивает его, как в Discord. */
  function section(key: string, title: string, people: PublicUser[]) {
    if (people.length === 0) return null;
    const closed = shut.has(key) && !query.trim();
    return (
      <div key={key} className="invite-section">
        <button
          type="button"
          className={`invite-section-head${closed ? " shut" : ""}`}
          aria-expanded={!closed}
          onClick={() =>
            setShut((previous) => {
              const next = new Set(previous);
              if (!next.delete(key)) next.add(key);
              return next;
            })
          }
        >
          {title}
          <IconChevronDown size={14} />
        </button>
        {!closed && people.map(row)}
      </div>
    );
  }

  return (
    <Dialog
      title={voice ? `Пригласить друзей в ${serverName}` : `Пригласить друзей на сервер ${serverName}`}
      onClose={onClose}
      actions={
        <Button type="button" variant="secondary" onClick={onClose}>
          Готово
        </Button>
      }
    >
      {channel &&
        (voice ? (
          <div className="dialog-sub invite-sub">
            Участники окажутся в <IconVoiceChannel size={12} />
            {channel.name}
          </div>
        ) : (
          <div className="dialog-sub">Участники окажутся в #{channel.name}</div>
        ))}

      <Input
        value={query}
        placeholder="Найти друзей"
        onChange={(event) => setQuery(event.target.value)}
        autoFocus
      />

      <div className="invite-friends">
        {shown.length === 0 ? (
          <div className="small muted invite-empty">
            {friends.length === 0
              ? "Друзей пока нет — отправьте ссылку ниже."
              : query.trim()
                ? "Никого не найдено"
                : voice
                  ? "Звать некого: все уже здесь"
                  : "Все ваши друзья уже на сервере"}
          </div>
        ) : voice ? (
          <>
            {section(
              "members",
              "Участники сервера",
              shown.filter((friend) => members.has(friend.id)),
            )}
            {section(
              "outside",
              "Пригласить на сервер",
              shown.filter((friend) => !members.has(friend.id)),
            )}
          </>
        ) : (
          shown.map(row)
        )}
      </div>

      <div className="invite-link-block">
        <div className="field-label">Или отправьте другу ссылку-приглашение на сервер</div>
        <div className="line" style={{ gap: 8 }}>
          <Input
            readOnly
            value={code ? inviteLink(code) : problem ? "" : "…"}
            onFocus={(event) => event.target.select()}
          />
          <Button type="button" disabled={!code} onClick={copy}>
            {copied ? "Скопировано" : "Копировать"}
          </Button>
        </div>
        {problem ? (
          <div className="small danger-text">{problem}</div>
        ) : (
          <div className="tiny muted">
            {link?.expires
              ? `Ссылка действует до ${new Date(link.expires).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}.`
              : link
                ? "Ссылка бессрочная."
                : ""}{" "}
            Управлять приглашениями — в настройках сервера.
          </div>
        )}
      </div>
    </Dialog>
  );
}
